import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { countryList } from '../constants/countries';
import CountrySelect from './CountrySelect';
import creditCardLogos from '../assets/credit-card-logos.png';
import securePaymentLock from '../assets/secure-payment-lock.png';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import LegalModal from './LegalModal';
import Checkbox from './Checkbox';
import InvestorGuaranteeShield from './signup/InvestorGuaranteeShield';
import { validateSignupEmail } from '../utils/validateSignupEmail';
import {
  DEFAULT_INVESTOR_PLAN,
  INVESTOR_GUARANTEE_LABEL,
  INVESTOR_PRICING_PLANS,
} from '../constants/investorPricingPlans';
import { loadPricingSignupDraft, savePricingSignupDraft } from '../utils/pricingSignupDraft';
import { signupButtonPhase } from '../utils/signupSessionGuards';

interface SignUpProps {
  userType: 'startup' | 'investor';
  pricingType?: 'yearly' | 'lifetime' | 'threeYear';
  onPromoCodeValid?: (isValid: boolean) => void;
  onCountryChange?: (country: string) => void;
}

interface CheckoutSessionResponse {
  success: boolean;
  sessionId?: string;
  url?: string;
  error?: string;
}

const STARTUP_PRIMARY_BUTTON_CLASS =
  'bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl disabled:shadow-none';

const INVESTOR_PRIMARY_BUTTON_CLASS =
  'bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl disabled:shadow-none';

const SignUp: React.FC<SignUpProps> = ({
  userType,
  pricingType = userType === 'investor' ? DEFAULT_INVESTOR_PLAN : 'lifetime',
  onPromoCodeValid,
  onCountryChange,
}) => {
  const { signUp, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [draft] = useState(() => loadPricingSignupDraft(userType));
  const [name, setName] = useState(draft?.name ?? '');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [country, setCountry] = useState(draft?.country ?? '');
  const [hearAboutUs, setHearAboutUs] = useState(draft?.hearAboutUs ?? '');
  const [localError, setLocalError] = useState('');
  const [agreeToNewsletter, setAgreeToNewsletter] = useState(draft?.agreeToNewsletter ?? true);
  const [agreeToPrivacyPolicy, setAgreeToPrivacyPolicy] = useState(
    draft?.agreeToPrivacyPolicy ?? false
  );
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValidation, setPromoCodeValidation] = useState<string | null>(null);
  const [promoSkipsPayment, setPromoSkipsPayment] = useState(false);
  const [promoTestCheckout, setPromoTestCheckout] = useState(false);
  const [validatingPromoCode, setValidatingPromoCode] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [legalModal, setLegalModal] = useState<{
    isOpen: boolean;
    type: 'terms' | 'privacy';
  }>({
    isOpen: false,
    type: 'terms',
  });
  const [fieldErrors, setFieldErrors] = useState<{
    name?: boolean;
    email?: boolean;
    country?: boolean;
    terms?: boolean;
  }>({});

  // Function to detect user's country based on IP (may fail on localhost due to CORS)
  const detectUserCountry = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      if (data.country_name && countryList.includes(data.country_name)) {
        setCountry(data.country_name);
        // console.log(`🌍 Detected country: ${data.country_name}`);
      }
    } catch {
      // CORS or network error (common on localhost) – user can select country manually
    }
  };

  // Restore name/email/country after Stripe cancel, but never lock a promo across visits.
  useEffect(() => {
    if (!draft?.country) {
      void detectUserCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  useEffect(() => {
    onCountryChange?.(country);
  }, [country, onCountryChange]);

  // Handle UTM parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');

    if (source) setUtmSource(source);
    if (medium) setUtmMedium(medium);

    if (campaign) {
      setUtmCampaign(campaign);
      // Auto-populate and validate promo code if it comes from campaign
      // Assuming campaign IS the promo code
      const upperCampaign = campaign.toUpperCase();
      setPromoCode(upperCampaign);
      // We need to call validatePromoCode but it uses the state setter which is fine
      // However, we should be careful about the dependency array or call it directly
      // Since validatePromoCode is defined in the component scope, we can call it.
      // We pass the campaign directly because state update for promoCode might not be reflected yet
      validatePromoCode(upperCampaign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const hearAboutUsOptions = [
    { value: 'word-of-mouth', label: 'Word of mouth' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'startup-event', label: 'Event or conference' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'google', label: 'Google' },
    { value: 'facebook-instagram', label: 'Facebook/Instagram' },
    { value: 'other', label: 'Other' },
  ];

  const validatePromoCode = async (code: string) => {
    if (!code.trim()) {
      setPromoCodeValidation(null);
      return;
    }

    try {
      setValidatingPromoCode(true);
      const validatePromoCodeFunction = httpsCallable(functions, 'validatePromoCode');
      const result = await validatePromoCodeFunction({ code: code.trim(), userType });

      const data = result.data as {
        success: boolean;
        exists: boolean;
        promoCode?: {
          discount?: number;
          skipCheckout?: boolean;
          testCheckout?: boolean;
        };
      };
      if (data.success && data.exists) {
        const skipsPayment =
          data.promoCode?.skipCheckout === true || data.promoCode?.discount === 100;
        setPromoCodeValidation('true');
        setPromoSkipsPayment(skipsPayment);
        setPromoTestCheckout(!skipsPayment && data.promoCode?.testCheckout === true);
        if (onPromoCodeValid) onPromoCodeValid(skipsPayment);
      } else {
        setPromoCodeValidation('false');
        setPromoSkipsPayment(false);
        setPromoTestCheckout(false);
        if (onPromoCodeValid) onPromoCodeValid(false);
        setShowToast(true);
        // Auto-hide toast after 3 seconds
        setTimeout(() => setShowToast(false), 3000);
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeValidation('false');
      setPromoSkipsPayment(false);
      setPromoTestCheckout(false);
      setShowToast(true);
      // Auto-hide toast after 3 seconds
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setValidatingPromoCode(false);
    }
  };

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setPromoCode(value);
    // Clear validation when user types
    setPromoCodeValidation(null);
    setPromoSkipsPayment(false);
    setPromoTestCheckout(false);
    if (onPromoCodeValid) onPromoCodeValid(false);
  };

  const handleApplyPromoCode = () => {
    if (promoCode.trim()) {
      validatePromoCode(promoCode);
    }
  };

  const handleClearPromoCode = () => {
    setPromoCode('');
    setPromoCodeValidation(null);
    setPromoSkipsPayment(false);
    setPromoTestCheckout(false);
    onPromoCodeValid?.(false);
  };

  // Calculate price based on userType and pricingType
  const getPrice = (): string => {
    if (userType === 'startup') {
      return pricingType === 'yearly' ? '$99' : '$149';
    }
    if (pricingType === 'yearly') return '$900';
    if (pricingType === 'threeYear') return '$1,800';
    return '$1,200';
  };

  const getPlanLabel = (): string => {
    if (userType === 'startup') {
      return pricingType === 'yearly' ? 'Yearly Access' : 'Lifetime Access';
    }
    if (pricingType === 'yearly') return 'Annual Access';
    if (pricingType === 'threeYear') return '3-Year Access';
    return 'Lifetime Access';
  };

  const getPaymentText = (): string => {
    if (pricingType === 'yearly') {
      return 'billed yearly';
    }
    if (pricingType === 'threeYear') return 'one payment for 3-year access';
    return 'one-time payment';
  };

  const handleContinue = async () => {
    clearError();
    setLocalError('');

    const emailCheck = validateSignupEmail(email);
    const nextErrors: {
      name?: boolean;
      email?: boolean;
      country?: boolean;
      terms?: boolean;
    } = {};
    if (!name.trim()) nextErrors.name = true;
    if (!emailCheck.ok) nextErrors.email = true;
    if (!country.trim()) nextErrors.country = true;
    if (!agreeToPrivacyPolicy) nextErrors.terms = true;

    setFieldErrors(nextErrors);

    if (nextErrors.name) {
      setLocalError('Name is required');
      return;
    }
    if (!emailCheck.ok) {
      setLocalError(emailCheck.message);
      return;
    }
    if (nextErrors.country) {
      setLocalError('Country is required');
      return;
    }
    if (nextErrors.terms) {
      setLocalError('Please accept the Terms and Conditions and Privacy Policy to continue.');
      return;
    }

    try {
      setSubmitting(true);
      // Create preview account without password
      const signUpResult = await signUp(email, userType, {
        name,
        country,
        hearAboutUs,
        agreeToNewsletter,
        agreeToPrivacyPolicy,
        promoCode: promoCodeValidation === 'true' ? promoCode : 'stripe',
        utmSource,
        utmMedium,
        utmCampaign,
      });

      if (!signUpResult || !signUpResult.userId) {
        throw new Error('Failed to create preview account. Please try again.');
      }

      const createdUserId = signUpResult.userId;

      // Free promo (including INTERNAL_TEST): skip Stripe. Paid tester promo still checks out.
      // Must click the verify-email link before creating a password / opening the profile form.
      if (promoCodeValidation === 'true' && promoSkipsPayment) {
        navigate(`/verify-email?userId=${encodeURIComponent(createdUserId)}`);
        return;
      }

      // No promo code: start Stripe Checkout flow
      try {
        setRedirectingToCheckout(true);
        savePricingSignupDraft({
          userType,
          pricingType,
          name,
          email,
          country,
          hearAboutUs,
          agreeToNewsletter,
          agreeToPrivacyPolicy,
        });

        const createCheckoutSessionFunction = httpsCallable(functions, 'createCheckoutSession');
        const sessionResult = await createCheckoutSessionFunction({
          userId: createdUserId,
          userType,
          pricingType,
          promoCode: promoCodeValidation === 'true' ? promoCode : undefined,
          cancelOrigin: window.location.origin,
        });
        const sessionData = sessionResult.data as CheckoutSessionResponse;

        if (!sessionData.success || !sessionData.url) {
          throw new Error(
            sessionData.error || 'Unable to start the checkout session. Please try again.'
          );
        }

        localStorage.setItem('pendingUserId', createdUserId);
        localStorage.setItem('pendingUserType', userType);
        window.location.href = sessionData.url;
      } catch (checkoutError) {
        console.error('Error creating checkout session:', checkoutError);
        const message =
          checkoutError instanceof Error
            ? checkoutError.message
            : 'Unable to redirect to checkout. Please try again.';
        setLocalError(message);
      } finally {
        setRedirectingToCheckout(false);
      }
    } catch (err) {
      // Error is handled by AuthContext
      console.error('Registration failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const nameHasError = !!fieldErrors.name;
  const emailHasError = !!fieldErrors.email || !!error;
  const countryHasError = !!fieldErrors.country;
  const termsHasError = !!fieldErrors.terms;

  const primaryButtonClass =
    userType === 'investor' ? INVESTOR_PRIMARY_BUTTON_CLASS : STARTUP_PRIMARY_BUTTON_CLASS;
  const fieldLabelClass =
    userType === 'investor'
      ? 'block text-lg font-normal text-white mb-1.5 text-left'
      : 'block text-xs font-medium text-white mb-1 text-left';

  return (
    <>
      <form
        className={userType === 'investor' ? 'space-y-3' : 'space-y-4 text-left'}
        onSubmit={e => e.preventDefault()}
        onKeyDown={e => {
          // Only the Continue / Join CTA should advance — not Enter in inputs
          if (e.key === 'Enter') {
            const target = e.target as HTMLElement;
            if (target.tagName === 'TEXTAREA') return;
            e.preventDefault();
          }
        }}
      >
        {/* Price and Promo Code Section */}
        <div className="flex items-start justify-between gap-2">
          {/* Price Section — do not shrink so “one-time payment” stays one line */}
          <div className="flex shrink-0 flex-col items-start text-left">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
              {getPlanLabel()}
            </span>
            <div className="flex flex-nowrap items-center gap-2">
              <span
                className={`text-2xl font-bold text-white ${
                  promoSkipsPayment || promoTestCheckout ? 'line-through' : ''
                }`}
                style={{
                  textDecorationColor:
                    promoSkipsPayment || promoTestCheckout ? '#ef4444' : undefined,
                }}
              >
                {getPrice()}
              </span>
              {userType === 'investor' &&
                pricingType === 'threeYear' &&
                !promoSkipsPayment &&
                !promoTestCheckout && (
                  <span className="inline-flex shrink-0 items-center self-center rounded-md bg-emerald-700 px-2 py-1 text-xs font-bold uppercase leading-none tracking-wide text-white">
                    Save $900
                  </span>
                )}
              {promoSkipsPayment && <span className="text-2xl font-bold text-white">FREE</span>}
              {promoTestCheckout && <span className="text-2xl font-bold text-white">$1</span>}
            </div>
            {userType === 'investor' && pricingType === 'threeYear' ? (
              <span className="text-sm leading-snug text-gray-400">
                <span className="block whitespace-nowrap">
                  {INVESTOR_PRICING_PLANS.threeYear.priceNoteLines?.[0]}
                </span>
                <span className="block whitespace-nowrap">
                  {INVESTOR_PRICING_PLANS.threeYear.priceNoteLines?.[1]}
                </span>
              </span>
            ) : (
              <span className="whitespace-nowrap text-sm text-gray-400 text-left">
                {getPaymentText()}
              </span>
            )}
          </div>

          <div className="relative min-w-0 flex-1 flex flex-col items-end">
            {promoCodeValidation === 'true' ? (
              <div className="mb-4 flex max-w-[11rem] flex-col items-end text-right animate-in fade-in zoom-in duration-300 sm:max-w-none">
                <div className="flex items-start justify-end gap-1.5 text-emerald-500">
                  <svg
                    className="mt-0.5 h-4 w-4 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs font-bold uppercase leading-tight tracking-wide">
                    {promoCode} accepted
                  </span>
                </div>
                <div className="text-gray-400 text-xs mt-0.5">
                  {promoSkipsPayment ? (
                    <>
                      Your cost is <span className="text-white font-bold">$0</span>
                    </>
                  ) : promoTestCheckout ? (
                    <>
                      Your cost is <span className="text-white font-bold">$1</span> on Stripe
                    </>
                  ) : (
                    <>Continue to Stripe to complete payment</>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleClearPromoCode}
                  className="mt-1 text-xs text-gray-400 underline decoration-gray-500 underline-offset-2 hover:text-white"
                >
                  Change promo
                </button>
              </div>
            ) : (
              <>
                {/* Toast Notification - Above the promo code input */}
                {showToast && (
                  <div className="absolute -top-8 right-0 z-50 animate-in slide-in-from-right duration-300">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-lg shadow-lg flex items-center space-x-2">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs font-medium">Invalid promo code</span>
                    </div>
                  </div>
                )}
                <div className="mb-1.5 flex flex-col items-end gap-1 sm:mb-2 sm:flex-row sm:items-center sm:gap-0 sm:space-x-2">
                  <label
                    htmlFor="promoCode"
                    className={`${
                      userType === 'investor' ? 'text-lg font-normal' : 'text-sm font-medium'
                    } whitespace-nowrap text-white`}
                  >
                    Promo code?
                  </label>
                  <input
                    id="promoCode"
                    type="text"
                    value={promoCode}
                    onChange={handlePromoCodeChange}
                    className="w-24 px-2 py-1 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent focus:ring-emerald-500 text-sm"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyPromoCode}
                  disabled={validatingPromoCode}
                  className={`${primaryButtonClass} py-1 px-3 text-sm`}
                >
                  {validatingPromoCode ? 'Validating...' : 'Apply'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Name Field */}
        <div>
          <label htmlFor="name" className={fieldLabelClass}>
            * Your Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value);
              if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: false }));
              if (error) clearError();
              if (localError) setLocalError('');
            }}
            className={`w-full px-3 py-1.5 bg-white border rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 ${
              nameHasError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-emerald-500'
            }`}
            placeholder="Enter your full name"
          />
        </div>

        {/* Company Email Field */}
        <div>
          <label htmlFor="email" className={fieldLabelClass}>
            * Company Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: false }));
              if (error) clearError();
              if (localError) setLocalError('');
            }}
            className={`w-full px-3 py-1.5 bg-white border rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 ${
              emailHasError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-emerald-500'
            }`}
            placeholder="Enter your company email"
          />
        </div>

        {/* Country Field */}
        <div>
          <label htmlFor="country" className={fieldLabelClass}>
            * Country
          </label>
          <CountrySelect
            id="country"
            value={country}
            onChange={nextCountry => {
              setCountry(nextCountry);
              if (fieldErrors.country) setFieldErrors(prev => ({ ...prev, country: false }));
              if (error) clearError();
              if (localError) setLocalError('');
            }}
            className={`w-full px-3 py-1.5 bg-white border rounded-lg text-black focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")] pr-10 [-webkit-appearance:none] [-moz-appearance:none] ${
              countryHasError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-emerald-500'
            }`}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px 16px',
              paddingRight: '40px',
            }}
          />
        </div>

        {/* How did you hear about us Field */}
        <div>
          <label htmlFor="hearAboutUs" className={fieldLabelClass}>
            How did you hear about us?
          </label>
          <select
            id="hearAboutUs"
            value={hearAboutUs}
            onChange={e => setHearAboutUs(e.target.value)}
            className={`w-full px-3 py-1.5 bg-white border rounded-lg text-black focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")] pr-10 [-webkit-appearance:none] [-moz-appearance:none] border-gray-300 focus:ring-emerald-500`}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px 16px',
              paddingRight: '40px',
            }}
          >
            <option value="" className="text-gray-900">
              Select an option
            </option>
            {hearAboutUsOptions.map(option => (
              <option key={option.value} value={option.value} className="text-gray-900">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Submit Button — click only; Enter in fields does not advance */}
        <div className="">
          <button
            type="button"
            onClick={handleContinue}
            disabled={signupButtonPhase(submitting, redirectingToCheckout) !== 'ready'}
            className={`w-full mt-4 mb-0 ${primaryButtonClass} inline-flex h-11 items-center justify-center gap-2 px-4 text-base leading-none`}
          >
            {signupButtonPhase(submitting, redirectingToCheckout) !== 'ready' ? (
              <div className="inline-flex items-center justify-center gap-2 leading-none">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {signupButtonPhase(submitting, redirectingToCheckout) === 'creating'
                  ? 'Creating Account...'
                  : 'Redirecting to Stripe...'}
              </div>
            ) : promoSkipsPayment ? (
              'Join PRAIRI for Free'
            ) : (
              <span className="inline-flex items-center justify-center gap-2 leading-none">
                <img
                  src={securePaymentLock}
                  alt="Secure payment"
                  className="h-5 w-5 shrink-0 object-contain sm:h-6 sm:w-6"
                />
                <span className="whitespace-nowrap leading-none">Continue to Secure Payment</span>
              </span>
            )}
          </button>
          {userType === 'investor' && (
            <div className="mt-2 flex items-center justify-center gap-2 text-sm font-medium leading-tight text-[#01A7E3]">
              <InvestorGuaranteeShield className="h-5 w-5 shrink-0" />
              <span>{INVESTOR_GUARANTEE_LABEL}</span>
            </div>
          )}
          {/* Error Message */}
          {(error || localError) && (
            <div className="">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-500 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-red-700 text-sm font-medium">{error || localError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Terms and Privacy Policy (required for GDPR) */}
        <div className={`mt-4 ${userType === 'startup' ? 'text-left' : ''}`}>
          <Checkbox
            checked={agreeToPrivacyPolicy}
            hasError={termsHasError}
            align={userType === 'startup' ? 'start' : 'center'}
            labelClassName={
              userType === 'startup' ? 'text-left text-xs leading-5 sm:text-sm' : undefined
            }
            onChange={checked => {
              setAgreeToPrivacyPolicy(checked);
              if (checked) {
                setFieldErrors(prev => ({ ...prev, terms: false }));
                setLocalError('');
              }
            }}
            label={
              <>
                I accept the{' '}
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    setLegalModal({ isOpen: true, type: 'terms' });
                  }}
                  style={{ color: '#00b0f0' }}
                  className="inline border-0 bg-transparent p-0 align-baseline leading-[inherit] hover:underline"
                >
                  Terms and Conditions
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    setLegalModal({ isOpen: true, type: 'privacy' });
                  }}
                  style={{ color: '#00b0f0' }}
                  className="inline border-0 bg-transparent p-0 align-baseline leading-[inherit] hover:underline"
                >
                  Privacy Policy
                </button>
              </>
            }
            id="agreeToPrivacyPolicy"
          />
        </div>

        {/* Newsletter (optional) */}
        <div className="mt-3">
          <Checkbox
            checked={agreeToNewsletter}
            onChange={setAgreeToNewsletter}
            label="Let me know about the latest investor and startup news, funding tips, exclusive events, webinars, and more."
            id="agreeToNewsletter"
          />
        </div>

        {/* Legal Modal */}
        <LegalModal
          isOpen={legalModal.isOpen}
          onClose={() => setLegalModal({ ...legalModal, isOpen: false })}
          type={legalModal.type}
        />

        {/* Credit Card Logos */}
        <div className={`flex justify-center ${userType === 'investor' ? 'mt-2' : 'mt-4'}`}>
          <img
            src={creditCardLogos}
            alt="PayPal, Mastercard, Visa, American Express, and Discover"
            className="h-8 opacity-80"
          />
        </div>
      </form>
    </>
  );
};

export default SignUp;
